import type {
  FolderSelection,
  PendingFileMetadata,
} from "./features/selection";
import type { FileEntry, FolderEntry } from "./fetchFiles";

/** The item being deselected out of an inherited (ancestor-folder) selection. */
export interface FolderSplitDeselect {
  type: "file" | "folder";
  fileId?: string;
  filePath: string[];
  driveId: string | null;
}

/** Resulting selection after splitting an ancestor folder to exclude one item. */
export interface FolderSplitResult {
  fileIds: string[];
  folderPaths: string[][];
  metadata: Map<string, PendingFileMetadata>;
}

/**
 * Loads the direct children of a folder. Returns null when the contents
 * could not be loaded (e.g. network failure), which callers treat as
 * "cannot split" rather than "empty folder".
 */
export type ExpansionLoader = (
  filePath: string[],
  driveId: string | null
) => Promise<{ files: FileEntry[]; folders: FolderEntry[] } | null>;

export interface ComputeFolderSplitParams {
  deselect: FolderSplitDeselect;
  currentFileIds: string[];
  currentFolderSelections: FolderSelection[];
  getExpansion: ExpansionLoader;
}

/**
 * Split a selected ancestor folder so a single descendant item can be
 * deselected. Removes the ancestor folder selection and re-adds all of its
 * children individually — sibling files as IDs, sibling sub-folders as folder
 * paths — except the path leading to the deselected item. Walks each level
 * between the ancestor and the target, loading folder contents on demand via
 * `getExpansion`.
 *
 * Returns null when no selected ancestor contains the item, or when a needed
 * folder's contents cannot be loaded — callers must treat null as "leave the
 * selection unchanged" rather than wiping it.
 */
export async function computeFolderSplit({
  deselect,
  currentFileIds,
  currentFolderSelections,
  getExpansion,
}: ComputeFolderSplitParams): Promise<FolderSplitResult | null> {
  const currentFolderPaths = currentFolderSelections.map((fs) =>
    fs.driveId ? [fs.driveId, ...fs.path] : fs.path
  );
  const itemFullPath = deselect.driveId
    ? [deselect.driveId, ...deselect.filePath]
    : deselect.filePath;

  // Find the ancestor folder selection that contains this child.
  const ancestorIdx = currentFolderPaths.findIndex(
    (p) =>
      p.length <= itemFullPath.length &&
      p.every((seg, i) => itemFullPath[i] === seg)
  );
  if (ancestorIdx === -1) return null;

  const ancestorSelection = currentFolderSelections[ancestorIdx];
  if (!ancestorSelection) return null;

  // Remove the ancestor from folder paths; start file IDs from current
  // selection, excluding the deselected file if applicable.
  const newFolderPaths = currentFolderPaths.filter((_, i) => i !== ancestorIdx);
  const newFileIds =
    deselect.type === "file" && deselect.fileId
      ? currentFileIds.filter((id) => id !== deselect.fileId)
      : [...currentFileIds];
  const metadata = new Map<string, PendingFileMetadata>();

  const ancestorApiPath = ancestorSelection.path;
  const driveId = ancestorSelection.driveId;
  const diffSegments = deselect.filePath.slice(ancestorApiPath.length);

  // For files we walk to the file's parent folder; for folders we stop one
  // level before (the folder itself is the last segment to skip).
  const maxLevels =
    deselect.type === "file"
      ? diffSegments.length
      : Math.max(0, diffSegments.length - 1);

  let walkPath = [...ancestorApiPath];

  for (let i = 0; i <= maxLevels; i++) {
    const expansion = await getExpansion(walkPath, driveId);
    // Could not load this level's contents — abort the split entirely rather
    // than returning a partial result that would drop the folder selection.
    if (!expansion) return null;

    const isLastLevel = i === maxLevels;
    const nextSegment = isLastLevel ? null : diffSegments[i];

    // Add sibling files (skip the deselected file at the last level).
    for (const f of expansion.files) {
      if (
        isLastLevel &&
        deselect.type === "file" &&
        f.fileId === deselect.fileId
      ) {
        continue;
      }
      if (!newFileIds.includes(f.fileId)) {
        newFileIds.push(f.fileId);
        metadata.set(f.fileId, { name: f.fileName, type: f.fileType });
      }
    }

    // Add sibling folders.
    for (const subfolder of expansion.folders) {
      const subName = subfolder.filePath[subfolder.filePath.length - 1] || "";

      // Skip the path leading to the deselected item.
      if (!isLastLevel && subName === nextSegment) {
        continue;
      }
      // Skip the deselected folder itself at the last level.
      if (
        isLastLevel &&
        deselect.type === "folder" &&
        JSON.stringify(subfolder.filePath) === JSON.stringify(deselect.filePath)
      ) {
        continue;
      }

      const subPath = subfolder.driveId
        ? [subfolder.driveId, ...subfolder.filePath]
        : subfolder.filePath;
      newFolderPaths.push(subPath);
    }

    if (isLastLevel || !nextSegment) break;
    walkPath = [...walkPath, nextSegment];
  }

  return { fileIds: newFileIds, folderPaths: newFolderPaths, metadata };
}
